import React, { useState, useEffect } from "react";
import { FileText, Plus, Edit, X, CheckCircle, Clock, AlertCircle, Eye, RefreshCw, Check, Printer } from "lucide-react";
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

const HighwayUnderSection21 = () => {
    const [records, setRecords] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showSection21, setShowSection21] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsData, setDetailsData] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [currentProposalId, setCurrentProposalId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [loadingId, setLoadingId] = useState(null);
    const [isProcessingDecision, setIsProcessingDecision] = useState(false);
    const { language, translations } = useLanguage();
    const [owners, setOwners] = useState([]);
    const [selectedOwnerId, setSelectedOwnerId] = useState("");
    const [ownerMessage, setOwnerMessage] = useState("");
    const [proposalData, setProposalData] = useState({});
    const [loadingRecords, setLoadingRecords] = useState(false);

    // LAO Decision states
    const [laoDecision, setLaoDecision] = useState('');
    const [selectedDecisionDate, setSelectedDecisionDate] = useState('');
    const [laoRemarks, setLaoRemarks] = useState('');

    // Notification state
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });

    const [fileUrlToView, setFileUrlToView] = useState(null);
    const [isPDFToView, setIsPDFToView] = useState(false);
    const [showDocumentViewer, setShowDocumentViewer] = useState(false);


    // User role
    const [userRole, setUserRole] = useState(() => {
        try {
            const userData = localStorage.getItem("userData");
            if (userData) {
                const user = JSON.parse(userData);
                return user.user_role || null;
            }
        } catch (error) {
            console.error("Error parsing user data:", error);
        }
        return null;
    });

    // Form state for claim
    const [formData, setFormData] = useState({
        claimant_name: '',
        claimant_address: '',
        claim_date: '',
        survey_no: '',
        claim_details: '',
        dist_name: '',
        taluka_name: '',
        village_name: '',
        document: null
    });

    // Auto-hide notifications after 3 seconds
    useEffect(() => {
        if (notification.show) {
            const timer = setTimeout(() => {
                setNotification({ ...notification, show: false });
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // ---- Proposal ID ----
    useEffect(() => {
        const getProposalInfo = () => {
            const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
            if (currentProposal) {
                try {
                    const proposalId = currentProposal?.data?.default_proposal ||
                        currentProposal?.data?.proposal_id ||
                        currentProposal?.default_proposal ||
                        currentProposal?.proposal_id ||
                        null;

                    const laoUserId = currentProposal?.data?.usr_id || currentProposal?.usr_id;
                    console.log("Current LAO User ID:", laoUserId);

                    return { proposalId };
                } catch (e) {
                    console.error('Error parsing current project data (getProposalInfo):', e);
                }
            }
            return { proposalId: null };
        };

        const proposalInfo = getProposalInfo();
        setCurrentProposalId(proposalInfo.proposalId);
        console.log('Current Project-Proposal Info:', proposalInfo);
    }, []);

    // ---- Project ID ----
    useEffect(() => {
        const getSelectedProject = () => {
            try {
                const storedProject = localStorage.getItem('selectedProject');
                if (storedProject) {
                    const project = JSON.parse(storedProject);
                    const projectIdFromStorage = project.id || project.project_id;
                    const projectName = project.project_name || '';

                    if (projectIdFromStorage && projectIdFromStorage !== currentProjectId) {
                        setCurrentProjectId(projectIdFromStorage);
                    }
                }
            } catch (error) {
                console.error('Error reading selected project:', error);
            }
        };

        getSelectedProject();
        window.addEventListener('storage', getSelectedProject);
        return () => window.removeEventListener('storage', getSelectedProject);
    }, [currentProjectId]);

    //  ----Fetch Gat No.----
    useEffect(() => {
        const fetchOwners = async () => {
            if (formData.gateNo && currentProjectId && currentProposalId) {
                try {
                    const res = await apiService.getVerifyOwnerSurveyNo(
                        formData.gateNo,
                        currentProjectId,
                        currentProposalId
                    );
                    console.log("Full API Response:", res);

                    if (res?.status === 200 && res?.verify && Array.isArray(res.data)) {
                        setOwners(res.data);
                        setOwnerMessage(""); // ✅ clear old messages
                    } else if (res?.status === 200 && !res?.verify && res?.message) {
                        setOwners([]);
                        setOwnerMessage("No owners found for this survey number.");
                    } else {
                        setOwners([]);
                        setOwnerMessage("No owner data available.");
                    }
                } catch (err) {
                    console.error("Error fetching owners:", err);
                    setOwners([]);
                    setOwnerMessage("Failed to load owner data.");
                }
            }
        };
        fetchOwners();
    }, [formData.gateNo, currentProjectId, currentProposalId]);

    const handleOwnerSelect = (e) => {
        const ownerId = e.target.value;
        setSelectedOwnerId(ownerId);

        const owner = owners.find(o => o.land_owner_id.toString() === ownerId);
        if (owner) {
            setFormData(prev => ({
                ...prev,
                claimant_name: owner.owner_name || "",
                claimant_address: owner.owner_address || ""
            }));
        }
    };

    // Fetch Section 21 Data - Only when both IDs are available
    const fetchClaims = async () => {
        if (!currentProjectId || !currentProposalId) {
            setLoading(true);
            setError(null);
            console.log("Missing IDs - ProjectId:", currentProjectId, "ProposalId:", currentProposalId);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log("Fetching claims with ProjectId:", currentProjectId, "ProposalId:", currentProposalId);
            const response = await apiService.getClaimsUnderSection21(
                currentProjectId,
                currentProposalId
            );

            if (response?.status === 200) {
                setRecords(response.data || []);
            } else {
                setError("Failed to fetch claims under Section 21");
            }
        } catch (err) {
            console.error("Error fetching Section 21 claims:", err);
            setError("Error fetching Section 21 claims: " + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        if (!currentProjectId || !currentProposalId) return;

        try {
            // setLoading(true);
            setLoadingRecords(true);
            const response = await apiService.getProposalById(currentProjectId, currentProposalId);

            const ownerResponse = await apiService.get("getAllOwner", {
                projectId: currentProjectId,
                proposalId: currentProposalId,
                isActive: 1
            });

            setProposalData(response.data);
            const proposalData = response.data;
            const landData = ownerResponse.landAcquisition;

            setFormData({
                email: proposalData.section_11_email || "",
                contactNo: proposalData.section_11_mobile_no || "",
                caseNumber: proposalData.acquisition_proposal_no || "05/2022-23 ",
                village: proposalData.village_name || "कवठळ",
                taluka: proposalData.taluka_name || "संग्रामपूर",
                district: proposalData.dist_name || "बुलढाणा",
                fileNumber: proposalData.section_21_1_bhusampadan_no || proposalData.section_11_bhusampadan_no || "जा.क्र.काव्हि/स.म.अ./उपजि.भू.सं.(इ.व.द.)/95/2025",
                header: proposalData.section_21_1_header || "उपजिल्हाधिकारी भूसंपादन (इ.व.द.), जिल्हाधिकारी कार्यालय, बुलढाणा यांचे मार्फत कलम 21(1) नुसार काढलेली जाहिर नोटीस",
                officename: proposalData.section_21_1_office_name || proposalData.section_11_office_name || "उपजिल्हाधिकारी भूसंपादन (इमारत व दळणवळण)",
                branchname: proposalData.section_21_1_branch_name || proposalData.section_11_branch_name || "जिल्हाधिकारी कार्यालय, बुलढाणा",
                section_21_1_date: proposalData.section_21_1_date ? new Date(proposalData.section_21_1_date).toISOString().split("T")[0] : "",
                area: proposalData.total_area || "",
                acquired_area: proposalData.acquired_area || "",
                section_21_1_notification: proposalData.section_21_1_notification || proposalData.section_19_notification || "",
                lastDate: proposalData.section_21_1_date ? new Date(proposalData.section_21_1_date).toLocaleDateString('en-GB').replace(/\//g, '/') : "",
                section_21_1_representative_address: proposalData.section_21_1_representative_address,
                section_21_1_submission_date: proposalData.section_21_1_submission_date
                    ? new Date(proposalData.section_21_1_submission_date).toISOString().split("T")[0]
                    : ""

            });
        } catch (err) {
            setError('Failed to fetch data: ' + err.message);
        } finally {
            setLoadingRecords(false);
        }
    };
    useEffect(() => {
        fetchData();
    }, [currentProjectId, currentProposalId]);
    const handleViewDocument = (urlOrFile) => {
        if (!urlOrFile) return;

        let url, isPdf;

        if (urlOrFile instanceof File) {
            url = URL.createObjectURL(urlOrFile);
            isPdf = urlOrFile.type === "application/pdf";
        } else {
            url = urlOrFile;
            isPdf = urlOrFile.toLowerCase().includes(".pdf");
        }

        setFileUrlToView(url);
        setIsPDFToView(isPdf);
        setShowDocumentViewer(true);
    };


    // Only fetch claims when both IDs are available
    useEffect(() => {
        if (currentProjectId && currentProposalId) {
            fetchClaims();
        }
    }, [currentProjectId, currentProposalId]);

    const handleAddNew = () => {
        setIsEditMode(false);
        setFormData({
            claimant_name: '',
            claimant_address: '',
            claim_date: '',
            survey_no: '',
            claim_details: '',
            dist_name: '',
            taluka_name: '',
            village_name: '',
            document: null
        });
        setShowModal(true);
    };

    // Handle view details - similar to ObjectionList
    const handleViewDetails = async (record) => {
        try {
            setLoadingId(record.claimant_id || record.claimantId);

            // Set the details data directly from the record
            setDetailsData(record);
            setShowDetailsModal(true);

            setNotification({
                show: true,
                type: 'success',
                message: 'Claim details loaded successfully'
            });

        } catch (error) {
            console.error('Error fetching claim details:', error);
            setNotification({
                show: true,
                type: 'error',
                message: 'Failed to load claim details'
            });
        } finally {
            setLoadingId(null);
        }
    };

    // Handle LAO decision save
    const handleSave = async (claimData) => {
        if (!selectedDecisionDate || !laoDecision || !laoRemarks) {
            setNotification({
                show: true,
                type: 'error',
                message: 'Please fill all required fields: Decision, Date, and Remarks'
            });
            return;
        }

        setIsProcessingDecision(true);
        try {
            const updateData = {
                lao_decision: laoDecision,
                last_upd_dt: selectedDecisionDate,
                claim_details: laoRemarks,
                // lao_decision: 'completed'
            };

            // Call API to update LAO decision
            const response = await apiService.patchUpdateClaimsUnderSection21(
                claimData.claimant_id || claimData.claimantId,
                updateData
            );

            // Update the records list
            const updatedRecords = records.map((rec) => {
                const recId = rec.claimant_id || rec.claimantId;
                const currentId = claimData.claimant_id || claimData.claimantId;

                return recId === currentId
                    ? { ...rec, ...updateData }
                    : rec;
            });
            setRecords(updatedRecords);

            // Update the details data
            setDetailsData({
                ...claimData,
                ...updateData
            });

            setNotification({
                show: true,
                type: 'success',
                message: 'LAO decision updated successfully'
            });

            // Reset form
            setLaoDecision('');
            setSelectedDecisionDate('');
            setLaoRemarks('');

        } catch (error) {
            console.error('Error updating LAO decision:', error);
            setNotification({
                show: true,
                type: 'error',
                message: 'Failed to update LAO decision'
            });
        } finally {
            setIsProcessingDecision(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'document') {
            setFormData(prev => ({ ...prev, [name]: files[0] }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // New Print Function
    const handlePrint = () => {
        if (!records || records.length === 0) {
            alert("No claim data available to print.");
            return;
        }
        // Optional: ensure these values exist before printing
        const bhusampadanNo = proposalData?.section_11_bhusampadan_no || "—";
        const district = proposalData?.dist_name || "—";
        const taluka = proposalData?.taluka_name || "—";
        const village = proposalData?.village_name || "—";

        const win = window.open("", "", "height=900,width=1200");
        win.document.write(`
      <html>
        <head>
          <title>Section 21 Claims List</title>
          <style>
             @page {
          size: A4 portrait;   /* Orientation */
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
        }
            table {
              border-collapse: collapse;
              width: 100%;
              margin-top: 10px;
            }
            th, td {
              border: 1px solid black;
              padding: 4px;
              text-align: center;
            }
              .title {
            text-align: center;
            font-weight: bold;
            margin-top: 10px;
            font-size: 16px;
            margin-bottom: 10px;
          } 
            .details {
            text-align: center;
            margin-bottom: 10px;
            line-height: 1.6;
          }
          .details .bhusampadan {
            display: block;
            font-weight: bold;
            margin-bottom: 4px;
          }
          .details .location-line {
            display: flex;
            justify-content: center;
            gap: 10px; /* space between District, Taluka, Village */
          }
          .details .location-line span {
            display: inline-block;
            min-width: 10px;
          }
          </style>
        </head>
        <body>
        <div class="details">
          <div class="bhusampadan">भूसंपादन क्रमांक: ${bhusampadanNo}</div>
          <div class="location-line">
            <span><b>जिल्हा:</b> ${district}</span>
            <span><b>तालुका:</b> ${taluka}</span>
            <span><b>गाव:</b> ${village}</span>
          </div>
        </div>
            <div class="title">
              ${translations[language]?.ClaimsUnderSection21 || "कलम २१ अंतर्गत दावे"}
            </div>

            <div className="mt-6">
              <table className="border border-black border-collapse w-full text-xs mt-2">
                <thead>
                  <tr>
                    <th>${translations[language]?.SrNo21 || "अ.क्र"}</th>
                    <th>${translations[language]?.ClaimantName || "दावा दाखल करणाऱ्याचे नाव"}</th>
                    <th>${translations[language]?.Address21 || "पत्ता"}</th>
                    <th>${translations[language]?.ClaimFilingDate || "दावा भूसंपादन अधिकारी यांच्या समोर दाखल केल्याची तारीख"}</th>
                    <th>${translations[language]?.ClaimGroupNumber || "दाव्याचा गट क्रमांक"}</th>
                    <th>${translations[language]?.ClaimDetails || "दाव्यातील तपशील"}</th>
                    <th>${translations[language]?.LAODecisionStatus || "भूसंपादन अधिकार्‍यांचा निर्णय स्थिती"}</th>
                  </tr>
                </thead>
                <tbody>
                  ${records.map((rec, index) => `
                    <tr>
                      <td>${index + 1}</td>
                      <td>${rec.claimant_name || 'N/A'}</td>
                      <td>${rec.claimant_address || 'N/A'}</td>
                      <td>${rec.claim_date ? new Date(rec.claim_date).toLocaleDateString("hi-IN") : 'N/A'}</td>
                      <td>${rec.survey_no || 'N/A'}</td>
                      <td>${rec.claim_details || 'N/A'}</td>
                      <td>${getStatusText(rec.lao_decision)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        </body>
      </html>
    `);
        win.document.close();
        win.print();
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!currentProjectId || !currentProposalId) {
            alert('Project ID and Proposal ID are required');
            return;
        }

        setSubmitLoading(true);
        setError(null);

        try {
            let response;

            if (isEditMode) {
                // ---------- UPDATE existing claim ----------
                const formDataToSend = new FormData();
                Object.keys(formData).forEach((key) => {
                    if (formData[key]) {
                        // ✅ map gateNo → survey_no before sending
                        if (key === "gateNo") {
                            formDataToSend.append("survey_no", formData[key]);
                        } else {
                            formDataToSend.append(key, formData[key]);
                        }
                    }
                });

                response = await apiService.patchUpdateClaimsUnderSection21(
                    selectedRecord.claimantId || selectedRecord.claimant_id,
                    formDataToSend
                );

            } else {
                // ---------- ADD a NEW claim ----------
                const fd = new FormData();
                fd.append('project_id', currentProjectId);
                fd.append('proposal_id', currentProposalId);

                Object.entries(formData).forEach(([key, value]) => {
                    if (value !== null && value !== '') {
                        // ✅ map gateNo → survey_no
                        if (key === "gateNo") {
                            fd.append("survey_no", value);
                        } else {
                            fd.append(key, value);
                        }
                    }
                });

                response = await apiService.postAddClaimsUnderSection21(fd);
            }

            if (response?.status === 200) {
                setShowModal(false);
                fetchClaims();
                alert(isEditMode ? 'दावा सफलतापूर्वक अपडेट किया गया!' : 'दावा सफलतापूर्वक जोड़ा गया!');
            } else {
                setError(response?.message || 'Failed to save claim');
            }
        } catch (err) {
            console.error('Error saving claim:', err);
            setError('Error saving claim: ' + (err.message || err));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            claimant_name: '',
            claimant_address: '',
            claim_date: '',
            survey_no: '',
            claim_details: '',
            dist_name: '',
            taluka_name: '',
            village_name: '',
            document: null
        });
        setSelectedRecord(null);
        setIsEditMode(false);
    };

    const getStatusIcon = (decision) => {
        if (decision === 'approved') {
            return <CheckCircle className="h-5 w-5 text-green-600" />;
        } else if (decision === 'rejected') {
            return <X className="h-5 w-5 text-red-600" />;
        } else if (decision === 'pending_documents') {
            return <FileText className="h-5 w-5 text-orange-600" />;
        } else if (decision === 'under_review') {
            return <AlertCircle className="h-5 w-5 text-blue-600" />;
        } else if (decision === 'completed') {
            return <CheckCircle className="h-5 w-5 text-green-600" />;
        }
        return <Clock className="h-5 w-5 text-yellow-600" />;
    };

    const getStatusText = (decision) => {
        switch (decision) {
            case 'approved':
                return 'स्वीकृत';
            case 'rejected':
                return 'अस्वीकृत';
            case 'pending_documents':
                return 'दस्तावेज रलंबित';
            case 'under_review':
                return 'समीक्षाधीन';
            case 'completed':
                return 'पूर्ण';
            default:
                return 'प्रलंबित';
        }
    };

    const getStatusColor = (decision) => {
        switch (decision) {
            case 'approved':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'pending_documents':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'under_review':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'completed':
                return 'bg-green-100 text-green-800 border-green-200';
            default:
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        }
    };

    const renderDocumentViewer = () => {
        if (!showDocumentViewer || !fileUrlToView) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[98vh] overflow-hidden">
                    <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
                        <h3 className="text-lg font-semibold">दस्तावेज पूर्वावलोकन</h3>
                        <button
                            onClick={() => setShowDocumentViewer(false)}
                            className="hover:text-gray-200"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                    <div className="p-4 h-[86vh] overflow-auto">
                        {isPDFToView ? (
                            <iframe src={fileUrlToView} className="w-full h-full" title="PDF Viewer" />
                        ) : (
                            <img src={fileUrlToView} alt="दस्तावेज़" className="max-h-[75vh] mx-auto" />
                        )}
                    </div>
                </div>
            </div>
        );
    };


    // Enhanced details modal similar to ObjectionList
    const renderDetailsModal = () => {
        if (!showDetailsModal || !detailsData) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
                    {/* Enhanced Modal Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-6">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-4">
                                <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                                    <FileText className="h-8 w-8 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-white">{translations[language]?.Section21ClaimDetails}</h3>
                                    <p className="text-blue-100 text-sm mt-1">{translations[language]?.CompleteClaimInfo}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Modal Content */}
                    <div className="overflow-y-auto max-h-[calc(95vh-200px)]">
                        {/* Prominent Information Cards */}
                        <div className="p-8 bg-gray-50 border-b border-gray-200">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <AlertCircle className="h-5 w-5 mr-2 text-blue-600" />
                                {translations[language]?.MainInfo}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* Claimant Name */}
                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <div className="text-blue-600">
                                            <FileText className="h-4 w-4" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-600">{translations[language]?.ClaimantName}</span>
                                    </div>
                                    <p className="text-gray-900 font-medium">{detailsData.claimant_name}</p>
                                </div>

                                {/* Claim Date */}
                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <div className="text-blue-600">
                                            <Clock className="h-4 w-4" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-600">{translations[language]?.ClaimFilingDate}</span>
                                    </div>
                                    <p className="text-gray-900 font-medium">
                                        {new Date(detailsData.claim_date).toLocaleDateString('hi-IN')}
                                    </p>
                                </div>

                                {/* Survey Number */}
                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <div className="text-blue-600">
                                            <FileText className="h-4 w-4" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-600">{translations[language]?.ClaimGroupNumber}</span>
                                    </div>
                                    <p className="text-gray-900 font-medium">{detailsData.survey_no}</p>
                                </div>

                                {/* LAO Decision Status */}
                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <div className="text-blue-600">
                                            <AlertCircle className="h-4 w-4" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-600">{translations[language]?.Status21}</span>
                                    </div>
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(detailsData.lao_decision)}`}>
                                        {getStatusText(detailsData.lao_decision)}
                                    </span>
                                </div>
                            </div>

                            {/* LAO Decision Section - Only show if status is pending and user is LAO */}
                            {((detailsData?.lao_decision === 'pending' || !detailsData?.lao_decision) && userRole === 3) ? (
                                <div className="mt-8 pt-6 border-t border-gray-200">
                                    <h5 className="text-md font-semibold text-gray-800 mb-4">{translations[language]?.GiveLAODecision}</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                                        {/* LAO Decision */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {translations[language]?.Decision21} *
                                            </label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm 
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                value={laoDecision}
                                                onChange={(e) => setLaoDecision(e.target.value)}
                                                disabled={isProcessingDecision}
                                            >
                                                <option value="">-- निर्णय निवडा *--</option>
                                                <option value="completed">मान्य</option>
                                                <option value="rejected">अमान्य</option>
                                            </select>
                                        </div>

                                        {/* Decision Date */}
                                        {laoDecision && (
                                            <div className="animate-fadeIn">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {translations[language]?.DecisionDate} *
                                                </label>
                                                <input
                                                    type="date"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm 
                          focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                    value={selectedDecisionDate}
                                                    onChange={(e) => setSelectedDecisionDate(e.target.value)}
                                                    disabled={isProcessingDecision}
                                                />
                                            </div>
                                        )}

                                        {/* LAO Remarks */}
                                        {laoDecision && selectedDecisionDate && (
                                            <div className="animate-fadeIn">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {translations[language]?.LAOComment} *
                                                </label>
                                                <textarea
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm 
                          focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                                                    rows="3"
                                                    value={laoRemarks}
                                                    onChange={(e) => setLaoRemarks(e.target.value)}
                                                    disabled={isProcessingDecision}
                                                    placeholder={translations[language]?.EnterDecisionReason}
                                                />
                                            </div>
                                        )}

                                        {/* Save Button */}
                                        {laoDecision && selectedDecisionDate && laoRemarks && (
                                            <div className="animate-fadeIn">
                                                <button
                                                    onClick={() => handleSave(detailsData)}
                                                    disabled={isProcessingDecision || !laoDecision || !selectedDecisionDate || !laoRemarks}
                                                    className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white 
                          font-medium rounded-lg hover:from-green-700 hover:to-green-800 
                          focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 
                          transition-all duration-200 disabled:from-gray-400 disabled:to-gray-500 
                          disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
                                                >
                                                    {isProcessingDecision ? (
                                                        <>
                                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                            {translations[language]?.Processing21}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Check className="h-4 w-4 mr-2" />
                                                            {translations[language]?.SaveDecision}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                // Show current LAO decision info for completed cases
                                detailsData?.lao_decision === 'completed' && (
                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="p-2 bg-green-100 rounded-lg">
                                                        <Check className="h-5 w-5 text-green-600" />
                                                    </div>
                                                    <div>
                                                        <h6 className="font-medium text-gray-900">
                                                            {translations[language]?.Status21}: {getStatusText(detailsData?.lao_decision)}
                                                        </h6>
                                                        {detailsData?.last_upd_dt && (
                                                            <p className="text-sm text-gray-500">
                                                                {translations[language]?.Date21}: {new Date(detailsData.last_upd_dt).toLocaleDateString('hi-IN')}
                                                            </p>
                                                        )}
                                                        {detailsData?.claim_details && (
                                                            <p className="text-sm text-gray-600 mt-1">
                                                                {translations[language]?.LAOComment}: {detailsData.claim_details}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            )}

                            {/* Additional Details Section */}
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <h5 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                                    <FileText className="h-4 w-4 mr-2 text-blue-600" />
                                    {translations[language]?.AdditionalDetails}
                                </h5>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Address */}
                                    <div className="bg-white rounded-xl p-5 border border-gray-200 hover:border-gray-300 transition-colors shadow-sm hover:shadow-md">
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                                    <FileText className="h-4 w-4" />
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    {translations[language]?.Address21}
                                                </label>
                                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                                    <p className="text-gray-800 font-medium">
                                                        {detailsData.claimant_address || 'उपलब्ध नहीं'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Claim Details */}
                                    <div className="bg-white rounded-xl p-5 border border-gray-200 hover:border-gray-300 transition-colors shadow-sm hover:shadow-md">
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                                    <FileText className="h-4 w-4" />
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    {translations[language]?.ClaimDetails}
                                                </label>
                                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                                    <p className="text-gray-800 font-medium">
                                                        {detailsData.claim_details || 'उपलब्ध नहीं'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Document Section */}
                                    <div className="bg-white rounded-xl p-5 border border-gray-200 hover:border-gray-300 transition-colors shadow-sm hover:shadow-md">
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                                    <FileText className="h-4 w-4" />
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    {translations[language]?.Documents21}
                                                </label>
                                                <div className="">
                                                    {detailsData.document ? (
                                                        <button
                                                            onClick={() => handleViewDocument(detailsData.document)}
                                                            className="flex items-center gap-1 px-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                            <span>{translations[language]?.View21}</span>
                                                        </button>
                                                    ) : (
                                                        <p className="text-gray-500">{translations[language]?.NotAvailable}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>



                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Enhanced Modal Footer */}
                    <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <div className="text-sm text-gray-500">
                                <Clock className="h-4 w-4 inline mr-1" />
                                {translations[language]?.LastUpdated21}: {new Date(detailsData.last_upd_dt).toLocaleDateString('hi-IN')}
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setShowDetailsModal(false)}
                                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                >
                                    {translations[language]?.Close21}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Show loading state if IDs are not available yet
    if (!currentProjectId || !currentProposalId) {
        return (
            <div className="p-6 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <div className="text-lg mt-2">{translations[language]?.LoadingProjectInfo}</div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Notification System */}
            {notification.show && (
                <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg max-w-md z-50 flex items-center ${notification.type === 'success' ? 'bg-green-100 border-l-4 border-green-500 text-green-700' :
                    notification.type === 'error' ? 'bg-red-100 border-l-4 border-red-500 text-red-700' :
                        'bg-blue-100 border-l-4 border-blue-500 text-blue-700'
                    } transform transition-transform duration-300 animate-slideInRight`}>
                    <div className="mr-3">
                        {notification.type === 'success' ? (
                            <svg className="h-6 w-6 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        ) : notification.type === 'error' ? (
                            <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                    </div>
                    <div>
                        <p className="font-bold">
                            {notification.type === 'success' ? 'Success!' :
                                notification.type === 'error' ? 'Error!' : 'Info'}
                        </p>
                        <p>{notification.message}</p>
                    </div>
                    <button
                        onClick={() => setNotification({ ...notification, show: false })}
                        className="ml-auto text-gray-600 hover:text-gray-800"
                    >
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <div className="text-center p-2 space-y-1">
                            {/* Line 1: Case Number */}
                            <p className="font-semibold">
                                भूसंपादन प्रकरण क्रमांक {proposalData?.section_11_bhusampadan_no}
                            </p>

                            {/* Line 2: Village + Taluka + District */}
                            <p className="font-semibold">
                                मौजे {formData.village} | ता. {formData.taluka} | जि. {formData.district}
                            </p>
                        </div>

                        <h2 className="text-xl font-semibold text-gray-800">
                            {translations[language]?.ClaimsUnderSection21}
                        </h2>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handlePrint}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg 
                           flex items-center space-x-2 transition-all duration-300 shadow-sm"
                            >
                                <Printer className="h-4 w-4" />
                                <span>{translations[language]?.Print21}</span>
                            </button>
                            {/* {userRole === 3 && ( */}
                            {/* Data Entry */}
                            {(userRole === 3 || userRole === 14) && (
                                <button
                                    onClick={handleAddNew}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg 
                         flex items-center space-x-2 transition-all duration-300 shadow-sm"
                                >
                                    <Plus className="h-4 w-4" />
                                    <span>{translations[language]?.AddNewClaim}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {loading && (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <p className="mt-2 text-gray-600">{translations[language]?.LoadingClaims}</p>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center">
                                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                                <p className="text-red-700">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Enhanced Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full border border-gray-200 border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="border-r border-gray-200 p-3 text-center font-medium text-gray-700">
                                        {translations[language]?.Proceedings}
                                    </th>
                                    <th className="border-r border-gray-200 p-3 text-left font-medium text-gray-700">
                                        {translations[language]?.ClaimantName}
                                    </th>
                                    <th className="border-r border-gray-200 p-3 text-left font-medium text-gray-700">
                                        {translations[language]?.Address21}
                                    </th>
                                    <th className="border-r border-gray-200 p-3 text-center font-medium text-gray-700">
                                        {translations[language]?.ClaimFilingDate}
                                    </th>
                                    <th className="border-r border-gray-200 p-3 text-center font-medium text-gray-700">
                                        {translations[language]?.ClaimGroupNumber}
                                    </th>
                                    <th className="border-r border-gray-200 p-3 text-left font-medium text-gray-700">
                                        {translations[language]?.ClaimDetails}
                                    </th>
                                    <th className="border-r border-gray-200 p-3 text-center font-medium text-gray-700">
                                        {translations[language]?.LAODecisionStatus}
                                    </th>

                                </tr>
                            </thead>
                            <tbody>
                                {records.length > 0 ? (
                                    records.map((rec, index) => (
                                        <tr key={rec.claimant_id || rec.claimantId || index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                            <td className="p-3 text-center border-r border-gray-200">
                                                <div className="flex space-x-1 justify-center">
                                                    <button
                                                        onClick={async () => {
                                                            setLoadingId(rec.claimant_id || rec.claimantId);
                                                            try {
                                                                await handleViewDetails(rec);
                                                            } finally {
                                                                setLoadingId(null);
                                                            }
                                                        }}
                                                        disabled={loadingId === (rec.claimant_id || rec.claimantId)}
                                                        className="p-2 bg-green-600 hover:bg-green-700 text-white rounded 
                                       transition-all duration-300 shadow-sm disabled:opacity-50"
                                                        title="विवरण देखें"
                                                    >
                                                        {loadingId === (rec.claimant_id || rec.claimantId) ? (
                                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Eye className="h-4 w-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="border-r border-gray-200 p-3">{rec.claimant_name}</td>
                                            <td className="border-r border-gray-200 p-3">{rec.claimant_address}</td>
                                            <td className="border-r border-gray-200 p-3 text-center">
                                                {new Date(rec.claim_date).toLocaleDateString("hi-IN")}
                                            </td>
                                            <td className="border-r border-gray-200 p-3 text-center">{rec.survey_no}</td>
                                            <td className="border-r border-gray-200 p-3">
                                                <div className="max-w-xs truncate" title={rec.claim_details}>
                                                    {rec.claim_details}
                                                </div>
                                            </td>
                                            <td className="border-r border-gray-200 p-3 text-center">
                                                <div className="flex items-center justify-center space-x-2">
                                                    {getStatusIcon(rec.lao_decision)}
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(rec.lao_decision)}`}>
                                                        {getStatusText(rec.lao_decision)}
                                                    </span>
                                                </div>
                                                {rec.lao_decision === 'completed' && rec.claim_details && (
                                                    <div className="mt-1">
                                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                            {translations[language]?.CommentAvailable}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>

                                        </tr>
                                    ))
                                ) : (
                                    !loading && (
                                        <tr>
                                            <td colSpan="7" className="text-center p-8 text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <FileText className="h-12 w-12 text-gray-300 mb-2" />
                                                    <p>{translations[language]?.NoClaimsFound}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Add/Edit Claim Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <h3 className="text-xl font-semibold text-gray-800">
                                {isEditMode ? 'दावा संपादित करें' : <span>{translations[language]?.AddNewClaim}</span>}
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700 p-1"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translations[language]?.ClaimGroupNumber} *
                                    </label>
                                    <input
                                        type="text"
                                        name="gateNo"
                                        value={formData.gateNo}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translations[language]?.ClaimantName} *
                                    </label>
                                    {owners.length > 0 ? (
                                        <select
                                            value={selectedOwnerId}
                                            onChange={handleOwnerSelect}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                        >
                                            <option value="">-- Select Owner --</option>
                                            {owners.map(owner => (
                                                <option key={owner.land_owner_id} value={owner.land_owner_id}>
                                                    {owner.owner_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : ownerMessage ? (
                                        <div className="text-red-600 text-sm font-medium">{ownerMessage}</div>
                                    ) : (
                                        <input
                                            type="text"
                                            name="claimant_name"
                                            value={formData.claimant_name}
                                            onChange={handleInputChange}
                                            placeholder="Enter claimant's full name"
                                            required
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translations[language]?.Address21} *
                                    </label>
                                    <input
                                        type="text"
                                        name="claimant_address"
                                        value={formData.claimant_address}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translations[language]?.ClaimFilingDate} *
                                    </label>
                                    <input
                                        type="date"
                                        name="claim_date"
                                        value={formData.claim_date}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translations[language]?.Documents21}
                                    </label>
                                    <input
                                        type="file"
                                        name="document"
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        accept=".pdf,.doc,.docx,.jpg,.png"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {translations[language]?.ClaimDetails} *
                                </label>
                                <textarea
                                    name="claim_details"
                                    value={formData.claim_details}
                                    onChange={handleInputChange}
                                    rows="4"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-6 border-t">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                >
                                    {translations[language]?.Cancel21}
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitLoading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                >
                                    {submitLoading
                                        ? (isEditMode ? 'अपडेट हो रहा है...' : <span>{translations[language]?.Adding21}</span>)
                                        : (isEditMode ? 'दावा अपडेट करें' : <span>{translations[language]?.AddClaim}</span>)
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {renderDetailsModal()}
            {renderDocumentViewer()}

        </div>
    );
};

export default HighwayUnderSection21;